import heartbeat from "flow/heartbeat";
import config from "flow/config";
import { Dictionary } from "flow/api/types";
import environment from "flow/configuration/environment";
import baseUrl from "flow/wrappers/baseUrl";
import type MyFlow from "flow/MyFlow";

let skipSessionCheck = false;

export function enableSessionChecks(enable: boolean) {
    skipSessionCheck = !enable;
}

let ongoingPromise: Promise<boolean> = undefined;
let baseUrlsToOpen: Dictionary<Promise<void>> = {};
let currentlyAssertingSession = false;

export function assertSession(myFlow: MyFlow, moduleBaseUrl: string): Promise<void> {
    if (skipSessionCheck || environment.get(environment.keys.sessionRecovery) !== true) {
        return Promise.resolve();
    }

    // Avoid opening multiple dialogs
    if (currentlyAssertingSession) {
        if (!baseUrlsToOpen[moduleBaseUrl]) {
            baseUrlsToOpen[moduleBaseUrl] = ongoingPromise.then(
                performHeartbeatIfNeeded.bind(undefined, moduleBaseUrl)
            );
        }

        return baseUrlsToOpen[moduleBaseUrl];
    }

    currentlyAssertingSession = true;

    ongoingPromise = recoverSessionIfNeeded(myFlow, moduleBaseUrl);

    baseUrlsToOpen[moduleBaseUrl] = ongoingPromise
        .then(performHeartbeatIfNeeded.bind(undefined, moduleBaseUrl))
        .finally(() => {
            currentlyAssertingSession = false;
            baseUrlsToOpen = {};
        });

    return baseUrlsToOpen[moduleBaseUrl];
}

function performHeartbeatIfNeeded(moduleBaseUrl: string, needsHeartbeat: boolean): Promise<void> {
    return needsHeartbeat
        ? heartbeat.tick(moduleBaseUrl, environment.get(environment.keys.heartbeatTimeout))
        : Promise.resolve();
}

async function recoverSessionIfNeeded(myFlow: MyFlow, moduleBaseUrl: string): Promise<boolean> {
    const webModuleNeedsRelogin = await heartbeat.weAreGettingRedirected(moduleBaseUrl);

    const flowNeedsRelogin =
        webModuleNeedsRelogin && (baseUrl === moduleBaseUrl || (await heartbeat.weAreGettingRedirected(baseUrl)));

    if (!flowNeedsRelogin) {
        currentlyAssertingSession = false;
        return false;
    }

    return await myFlow.showCustomDialog<boolean>(config.componentIds.loginDialog);
}

export async function doWeStillHaveSession(): Promise<boolean> {
    if (skipSessionCheck || environment.get(environment.keys.sessionRecovery) !== true) {
        return true;
    }

    await heartbeat.tick(baseUrl, environment.get(environment.keys.heartbeatTimeout));
    return !(await heartbeat.weAreGettingRedirected(baseUrl));
}
